/**
 * Chat playground — a conversational RAG interface. Each turn sends the user's
 * question with a chat_prompt; the upstream generates an answer grounded in
 * retrieved chunks, which we display alongside citations.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Send, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { playgroundApi, type SearchChunk } from "@/lib/playground-api";
import { tenantApi, type Tenant } from "@/lib/proxy-api";
import { useHasInstance } from "@/lib/use-instance";
import { ApiException } from "@/lib/api-client";
import { useTenantWorkspace } from "@/lib/tenant-workspace";

interface Message {
  role: "user" | "assistant";
  content: string;
  citations?: SearchChunk[];
}

export function PlaygroundChatPage() {
  const hasInstance = useHasInstance();
  const workspace = useTenantWorkspace();
  const { data: tenantData } = useQuery({ queryKey: ["tenants"], queryFn: () => tenantApi.list(), enabled: hasInstance });
  const tenants: Tenant[] = tenantData?.business ?? [];
  const tenantOptions: Tenant[] = tenants.length > 0
    ? tenants
    : workspace.tenantId
      ? [{ id: workspace.tenantId, name: workspace.tenantName ?? `Tenant ${workspace.tenantId}` } as Tenant]
      : [];

  const [tenantId, setTenantId] = useState(() => workspace.isSuper ? "" : workspace.tenantId ?? "");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!workspace.isSuper && workspace.tenantId && tenantId !== workspace.tenantId) {
      setTenantId(workspace.tenantId);
    }
  }, [tenantId, workspace.isSuper, workspace.tenantId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const chatMut = useMutation({
    mutationFn: (text: string) =>
      playgroundApi.chatStored({
        tenantId,
        query: text,
        limit: 5,
        chat_prompt: text,
        system_messages: ["Answer the user's question concisely using only the retrieved context. If the context is insufficient, say so."],
      }),
    onSuccess: (res) => {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.answer ?? res.chat_response ?? "(no answer)", citations: res.results ?? res.chunks },
      ]);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiException ? err.message : "Chat failed"),
  });

  function send() {
    const text = input.trim();
    if (!tenantId || !text || chatMut.isPending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    chatMut.mutate(text);
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Chat" description="Conversational RAG over the tenant's knowledge base." />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="c-tenant">Tenant</Label>
            <select id="c-tenant" className="h-9 min-w-48 rounded-md border border-input bg-transparent px-3 text-sm"
              value={tenantId} onChange={(e) => { setTenantId(e.target.value); setMessages([]); }} disabled={!workspace.isSuper}>
              <option value="">Select…</option>
              {tenantOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {tenantId && (
        <div className="flex flex-col h-[calc(100vh-14rem)] rounded-lg border">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">Ask a question to start the conversation.</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted rounded-bl-sm"
                }`}>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.citations && m.citations.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/40 space-y-1">
                      <p className="text-xs opacity-70">Citations</p>
                      {m.citations.map((c, j) => (
                        <p key={j} className="text-xs opacity-80">• {c.filename ?? c.document_id} {c.score !== undefined ? `(${c.score.toFixed(2)})` : ""}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatMut.isPending && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message…"
              disabled={chatMut.isPending}
            />
            <Button onClick={send} disabled={!tenantId || !input.trim() || chatMut.isPending}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
          {error && <p className="px-4 pb-2 text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}
